import { action, observable, computed } from 'mobx';
import flatMap from 'lodash/flatMap';
import { find } from 'lodash';
import { DateTime } from 'luxon';
import LiveClassStore from 'stores/LiveClass';
import { extractFromSlug } from 'helpers/utils';
import LiveClassOfferingFilterStore from 'stores/LiveClassOfferingFilter';
import {
  EnhancedLiveClassOffering,
  LiveClassScheduleSortFields,
  LiveClassOfferingAggregate,
} from 'types/liveClass';
import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';

import { VocabularyItem } from 'types';

interface SubmitProgress {
  title?: string;
  hasError?: boolean;
  message?: string;
}

class LiveClassOfferingStore {
  @observable ready = false;

  @observable submitting = false;

  @observable submitProgress: SubmitProgress = {};

  @observable offeringSlug = '';

  @observable currentPageInternal = 1;

  @observable isSortAscending = true;

  @observable sortByField = LiveClassScheduleSortFields.StartTime;

  @observable entriesPerPage = 10;

  @observable selectedOfferingToEnroll = '';

  @observable languages: VocabularyItem[] = [];

  @observable showSubscriptionExpiredModal = false;

  @observable subscriptionExpiredEntitlementEndTime = '';

  liveClassOfferingFilterStore: LiveClassOfferingFilterStore;

  liveClassStore: LiveClassStore;

  constructor(
    liveClassOfferingFilterStore: LiveClassOfferingFilterStore,
    liveClassStore: LiveClassStore,
  ) {
    this.liveClassOfferingFilterStore = liveClassOfferingFilterStore;
    this.liveClassStore = liveClassStore;
    this.initSelectedOfferingToEnroll();
  }

  // eslint-disable-next-line class-methods-use-this
  extendLiveClassOffering = (
    offerings: EnhancedLiveClassOffering[],
    selectedOfferingId: string,
  ) =>
    offerings?.map((offering) => ({
      ...offering,
      // eslint-disable-next-line camelcase
      time: DateTime.now().set({
        hour: DateTime.fromISO(offering.start_time).get('hour'),
        minute: DateTime.fromISO(offering.start_time).get('minute'),
        second: DateTime.fromISO(offering.start_time).get('second'),
      }),
      selected: offering.offering_id === selectedOfferingId,
      instructorNames:
        offering?.instructors?.length === 0
          ? ''
          : flatMap(offering?.instructors, 'name')?.sort()?.join(', '),
    }));

  @action initSelectedOfferingToEnroll() {
    this.selectedOfferingToEnroll = this.enrolledOfferingId;
  }

  @action filterToApply = (s: LiveClassOfferingAggregate) => {
    if (!s) {
      return false;
    }
    return (
      // eslint-disable-next-line camelcase
      s?.offering_code === this.offeringCode &&
      DateTime.fromISO(s.enrollment_end_time).diff(DateTime.now()).toMillis() >
        0
    );
  };

  @computed get offeringCode() {
    if (this.offeringSlug) {
      const { code } = extractFromSlug(this.offeringSlug);
      return code;
    }
    return '';
  }

  @computed get scheduledOfferingBySlug() {
    const filteredOffering =
      this.offeringSlug !== ''
        ? this.liveClassOfferingFilterStore?.filteredEntries?.filter((s) =>
            this.filterToApply(s),
          )
        : [];

    const liveCLassOffering = this.extendLiveClassOffering(
      filteredOffering,
      this.selectedOfferingToEnroll,
    );

    const sortedOffering = this.sortLiveClassOffering(
      liveCLassOffering,
      this.isSortAscending,
      this.sortByField,
    );

    return sortedOffering;
  }

  @action onSortBy = (sortbyField: string) => {
    this.isSortAscending = !this.isSortAscending;
    this.sortByField = sortbyField as LiveClassScheduleSortFields;
  };

  // eslint-disable-next-line class-methods-use-this
  @action sortOffering = (
    a: EnhancedLiveClassOffering,
    b: EnhancedLiveClassOffering,
    sortByField: string,
  ) => {
    if (
      typeof a[sortByField] === 'number' &&
      typeof b[sortByField] === 'number'
    )
      return a[sortByField] - b[sortByField];

    return b[sortByField].localeCompare(a[sortByField]);
  };

  @action sortLiveClassOffering = (
    offerings: any,
    isSortAscending: boolean,
    sortByField: string,
  ) => {
    if (!isSortAscending) {
      return offerings
        .slice()
        .sort((a: any, b: any) =>
          sortByField === LiveClassScheduleSortFields.StartTime ||
          sortByField === LiveClassScheduleSortFields.EndTime
            ? new Date(b[sortByField]).getTime() -
              new Date(a[sortByField]).getTime()
            : this.sortOffering(b, a, sortByField),
        );
    }

    return offerings
      .slice()
      .sort((a: any, b: any) =>
        sortByField === LiveClassScheduleSortFields.StartTime ||
        sortByField === LiveClassScheduleSortFields.EndTime
          ? new Date(a[sortByField]).getTime() -
            new Date(b[sortByField]).getTime()
          : this.sortOffering(a, b, sortByField),
      );
  };

  @action onSelectOfferingToSchedule = (offeringId: string) => {
    this.selectedOfferingToEnroll = offeringId;
  };

  @action enrollUser = async (t, reschedule = false) => {
    let title: string;
    try {
      this.submitting = true;
      this.submitProgress = {};
      const response = await this.liveClassStore.enrollUser(
        this.selectedOfferingToEnroll,
        this.enrolledOfferingId,
      );

      title = reschedule
        ? t('Successfully rescheduled')
        : t('Successfully scheduled');

      const successMessage = reschedule
        ? t('You have rescheduled a live class successfully!')
        : t('You have scheduled a live class successfully!');

      this.submitProgress = {
        title,
        hasError: !response.offeringId,
        message: successMessage,
      };
    } catch (e) {
      title = reschedule
        ? t('Cannot reschedule live class')
        : t('Cannot schedule live class');
      const errorMessage = e.response.data?.detail || '';
      this.submitProgress = {
        title,
        hasError: true,
        message: LiveClassStore.getUseLiveClassEnrollErrorMessage(
          errorMessage,
          t,
        ),
      };
    } finally {
      this.submitting = false;
    }
    return this.submitProgress;
  };

  @computed get canShowCancelButton() {
    return (
      this.canShowRescheduleButton && this.liveClassStore.isPartnerScheduling
    );
  }

  @computed get isAllowToCancel() {
    if (this.liveClassStore.isPartnerScheduleReadOnly) {
      return false;
    }
    return !this.isCurrentlyScheduledClassTooSoon && !this.submitting;
  }

  @action cancelUser = async (t) => {
    try {
      this.submitting = true;
      this.submitProgress = {};
      await this.liveClassStore.unenrollPartnerUser(this.enrolledOfferingId);

      this.submitProgress = {
        title: t('Successfully canceled'),
        hasError: false,
        message: t('You have canceled a live class successfully!'),
      };
      this.selectedOfferingToEnroll = '';
    } catch (e) {
      const errorMessage = e.response?.data?.detail || '';
      this.submitProgress = {
        title: t('Could not cancel class'),
        hasError: true,
        message: LiveClassStore.getUseUnEnrollErrorMessage(errorMessage, t),
      };
    } finally {
      this.submitting = false;
    }
    return this.submitProgress;
  };

  @computed get paginatedEntries() {
    const startIndex = (this.currentPage - 1) * this.entriesPerPage;
    return this.scheduledOfferingBySlug.slice(
      startIndex,
      startIndex + this.entriesPerPage,
    );
  }

  @computed get totalPages() {
    return (
      Math.ceil(this.scheduledOfferingBySlug.length / this.entriesPerPage) || 1
    );
  }

  @computed get currentPage() {
    if (this.currentPageInternal > this.totalPages) {
      return 1;
    }

    return this.currentPageInternal;
  }

  @action setCurrentPage = (page = 1) => {
    this.currentPageInternal = page;
  };

  @computed get enrolledOffering() {
    if (!this.liveClassStore.offeringEnrolledLiveClassesLoaded) {
      return '';
    }
    const offering = this.liveClassStore.offeringEnrolledLiveClass;

    if (offering?.length) {
      return offering[0];
    }
    return '';
  }

  @computed get enrolledOfferingId() {
    const offering = this.enrolledOffering;
    if (offering) {
      return offering.offering_id;
    }
    return '';
  }

  @computed get selectedOfferingToSchedule() {
    if (!this.selectedOfferingToEnroll) return null;

    return find(
      this.liveClassStore.scheduledOfferingLiveClasses,
      (o) => o.offering_id === this.selectedOfferingToEnroll,
    );
  }

  @computed get isSelectedOfferingInCancellationPeriod() {
    const selectedOffering: any = this.selectedOfferingToSchedule;

    if (!selectedOffering) return false;

    const { start_time: startTime } = selectedOffering;
    return (
      DateTime.now() <
      DateTime.fromISO(startTime).minus({
        days: LIVE_CLASS_CANCELLATION_WINDOW,
      })
    );
  }

  @computed get canShowScheduleButton() {
    if (!this.liveClassStore.offeringEnrolledLiveClassesLoaded) {
      return false;
    }

    return Boolean(!this.enrolledOfferingId);
  }

  @computed get canShowRescheduleButton() {
    if (!this.enrolledOffering) {
      return false;
    }

    return true;
  }

  /**
   * True if the currently scheduled class is too soon to reschedule (within the cancellation window).
   */
  @computed get isCurrentlyScheduledClassTooSoon() {
    const enrolled = this.enrolledOffering;
    if (!enrolled || typeof enrolled !== 'object') return false;
    const { start_time: startTime } = enrolled;
    if (!startTime) return false;
    return (
      DateTime.now() >
      DateTime.fromISO(startTime).minus({
        days: LIVE_CLASS_CANCELLATION_WINDOW,
      })
    );
  }

  @computed get isAllowToSchedule() {
    if (this.liveClassStore.isPartnerScheduleReadOnly) {
      return false;
    }
    if (
      this.selectedOfferingToEnroll === this.enrolledOfferingId ||
      this.selectedOfferingToEnroll === '' ||
      this.liveClassStore.isAnyQuotaReached ||
      !this.liveClassStore.canScheduleLiveClass
    ) {
      return false;
    }

    return this.selectedOfferingToEnroll !== '' && !this.submitting;
  }

  @computed get isAllowToReSchedule() {
    if (this.liveClassStore.isPartnerScheduleReadOnly) {
      return false;
    }
    if (
      this.selectedOfferingToEnroll === this.enrolledOfferingId ||
      this.selectedOfferingToEnroll === ''
    ) {
      return false;
    }

    if (this.isCurrentlyScheduledClassTooSoon) {
      return false;
    }

    return this.selectedOfferingToEnroll !== '' && !this.submitting;
  }
}

export default LiveClassOfferingStore;
